import React, { useState, useEffect, useRef } from "react";
import {
  useSortingStateQuery,
  useLoadOrderMutation,
  useSortItemMutation,
  useUnSortItemMutation,
  useChangeDividerMutation,
  useEmptyDividerMutation,
  useEmptyAllMutation,
} from "@queries/sorting";

export default function OrderSortingPage() {
  const [orderQuery, setOrderQuery] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [currentDivider, setCurrentDivider] = useState(null);

  const { data: dividers = [], refetch: refetchState } = useSortingStateQuery();
  
  const loadOrder = useLoadOrderMutation();
  const sortItem = useSortItemMutation();
  const unsortItem = useUnSortItemMutation();
  const changeDivider = useChangeDividerMutation();
  const emptyDivider = useEmptyDividerMutation();
  const emptyAll = useEmptyAllMutation();

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!orderQuery.trim()) return;

    try {
      const res = await loadOrder.mutateAsync(orderQuery);
      setOrderData(res.order);
      setCurrentDivider(res.divider);
    } catch (err) {
      alert("Order not found or no dividers available");
      setOrderData(null);
      setCurrentDivider(null);
    }
  };

  const handleSortItem = async (product) => {
    try {
      const res = await sortItem.mutateAsync(product.order_product_id);
      
      // Update local state to reflect UI changes instantly
      setOrderData(prev => {
        if (!prev) return prev;
        const products = prev.products.map(p => 
          p.order_product_id === product.order_product_id ? res.product : p
        );
        return { ...prev, products };
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleUnSortItem = async (product) => {
    try {
      const res = await unsortItem.mutateAsync(product.order_product_id);
      
      // Update local state to reflect UI changes instantly
      setOrderData(prev => {
        if (!prev) return prev;
        const products = prev.products.map(p => 
          p.order_product_id === product.order_product_id ? res.product : p
        );
        return { ...prev, products };
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleChangeDivider = async (number) => {
    if (!orderData) return;
    
    try {
      await changeDivider.mutateAsync({ orderId: orderData.order_id, divider_number: number });
      setCurrentDivider(number);
      refetchState();
    } catch (err) {
      alert("Error changing divider");
    }
  };

  const handleEmptyDivider = async (number) => {
    if (!window.confirm(`Empty divider ${number}?`)) return;
    await emptyDivider.mutateAsync(number);
    if (currentDivider === number) {
      setOrderData(null);
      setCurrentDivider(null);
    }
  };

  const handleEmptyAll = async () => {
    if (!window.confirm("Empty ALL dividers?")) return;
    await emptyAll.mutateAsync();
    setOrderData(null);
    setCurrentDivider(null);
  };

  // Check if fully sorted
  const isOrderFullySorted = React.useMemo(() => {
    if (!orderData || !orderData.products) return false;
    return orderData.products.every(p => {
      const max = Math.max(1, p.item_count) * Math.max(1, p.quantity);
      return p.sorted_items_count >= max;
    });
  }, [orderData]);

  // Automatically focus on the order input
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div style={{ padding: 20, fontFamily: "sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>Order Sorting</h1>
        <button onClick={handleEmptyAll} style={{ padding: "8px 16px", background: "#e74c3c", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}>
          Empty All Dividers
        </button>
      </div>

      {/* order search */}
      <form onSubmit={handleOrderSubmit} style={{ display: "flex", gap: 10, margin: "20px 0" }}>
        <input 
          ref={inputRef}
          type="text" 
          value={orderQuery}
          onChange={e => setOrderQuery(e.target.value)}
          placeholder="Scan Order ID" 
          style={{ fontSize: 18, padding: 10, width: 300 }}
        />
        <button type="submit" style={{ fontSize: 18, padding: "10px 20px", cursor: "pointer" }}>Load</button>
      </form>

      {/* order details block */}
      {orderData && (
        <div style={{ 
          border: `2px solid ${isOrderFullySorted ? "#2ecc71" : "#3498db"}`, 
          background: isOrderFullySorted ? "#eafaf1" : "#ebf5fb",
          padding: 20, 
          borderRadius: 8, 
          marginBottom: 30 
        }}>
          <h2>Order #{orderData.order_id} - Assiged to Divider {currentDivider}</h2>
          {isOrderFullySorted && <h3 style={{ color: "#2ecc71" }}>All items are fully sorted!</h3>}
          <div style={{ marginTop: 20 }}>
            {orderData.products.map(p => {
              const max = Math.max(1, p.item_count) * Math.max(1, p.quantity);
              const sorted = p.sorted_items_count || 0;
              const isProductSorted = sorted >= max;

              return (
                <div key={p.order_product_id} style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  padding: 10,
                  borderBottom: "1px solid #ccc",
                  background: isProductSorted ? "#d5f5e3" : "transparent"
                }}>
                  <div>
                    <strong>{p.name}</strong> 
                    <span style={{ marginLeft: 10, color: "#666" }}>Model: {p.model}</span>
                    <span style={{ marginLeft: 10, color: "#666" }}>Barcode: {p.barcode}</span>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
                    <div style={{ fontSize: 18, fontWeight: "bold" }}>
                      Sorted: <span style={{ color: isProductSorted ? "green" : "red" }}>{sorted} / {max}</span>
                    </div>
                    {sorted > 0 && (
                      <button onClick={() => handleUnSortItem(p)} style={{ fontSize: 18, padding: "5px 15px", cursor: "pointer", background: "#e74c3c", color: "white", border: "none", borderRadius: 4 }}>-</button>
                    )}
                    {!isProductSorted && (
                      <button onClick={() => handleSortItem(p)} style={{ fontSize: 18, padding: "5px 15px", cursor: "pointer", background: "#f39c12", color: "white", border: "none", borderRadius: 4 }}>+</button>
                    )}
                    {isProductSorted && (
                      <span style={{ fontWeight: "bold", color: "#2ecc71", width: 44, textAlign: "center" }}>✓</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dividers Grid */}
      <h3>Dividers (100)</h3>
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(4, 1fr)", 
        gap: 15 
      }}>
        {dividers.map(d => {
          const isCurrent = currentDivider === d.number;
          const isOccupied = !!d.order_id;
          
          let bgColor = "#f9f9f9";
          if (isCurrent) bgColor = "#f1c40f"; // active loaded order
          else if (isOccupied) bgColor = "#bdc3c7"; // taken by other order

          return (
            <div key={d.number} style={{ 
              border: "1px solid #ccc", 
              borderRadius: 8, 
              padding: 15,
              background: bgColor,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10
            }}>
              <div style={{ fontSize: 24, fontWeight: "bold" }}>{d.number}</div>
              
              <div style={{ minHeight: 20, width: "100%", textAlign: "center" }}>
                {isOccupied ? <strong>Order #{d.order_id}</strong> : <span style={{ color: "#aaa" }}>Empty</span>}
              </div>

              {isOccupied && d.products && (
                <div style={{ width: "100%", fontSize: 13, background: "#fff", padding: 8, borderRadius: 4, maxHeight: 150, overflowY: "auto", boxSizing: "border-box" }}>
                  {d.products.map((p, idx) => {
                    const isComplete = p.sorted >= p.max;
                    return (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", color: isComplete ? "#2ecc71" : "#333", marginBottom: 4 }}>
                        <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", marginRight: 8 }} title={p.name}>{p.name}</span>
                        <span style={{ fontWeight: isComplete ? "bold" : "normal", whiteSpace: "nowrap" }}>{p.sorted}/{p.max}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {orderData && !isCurrent && (
                <button onClick={() => handleChangeDivider(d.number)} style={{ cursor: "pointer", padding: "4px 8px" }}>
                  Move Here
                </button>
              )}

              {isOccupied && (
                <button onClick={() => handleEmptyDivider(d.number)} style={{ cursor: "pointer", padding: "4px 8px", background: "#e74c3c", color: "white", border: "none", borderRadius: 4 }}>
                  Empty
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}